/**
 * Device State Manager
 * 
 * Centraliserad hantering av enhetsval i PWA Handel.
 * Säkerställer konsistent state mellan localStorage och sessionStorage.
 */

const STORAGE_KEYS = {
  CART_DEVICE_ID: 'trade-cart-deviceId',
  SELECTED_DEVICE_ID: 'selected_device_id',
  ONBOARDING_DEVICE_CODE: 'onboarding_device_code',
  SELECTED_DEVICE_CODE: 'selectedDeviceCode',
  SELECTED_DEVICE_NAME: 'selected_device_name',
} as const;

interface DeviceInfo {
  deviceId: string;
  deviceCode?: string;
  deviceName?: string;
}

/**
 * Sparar enhets-ID i alla relevanta storage layers
 */
export function saveDeviceId(info: DeviceInfo): void {
  const { deviceId, deviceCode, deviceName } = info;
  
  console.log('[DeviceStateManager] Saving device:', deviceId);
  
  try {
    // Uppdatera alla storage layers
    localStorage.setItem(STORAGE_KEYS.CART_DEVICE_ID, deviceId);
    if (deviceCode) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_DEVICE_CODE, deviceCode);
    }
    
    sessionStorage.setItem(STORAGE_KEYS.SELECTED_DEVICE_ID, deviceId);
    if (deviceCode) {
      sessionStorage.setItem(STORAGE_KEYS.ONBOARDING_DEVICE_CODE, deviceCode);
    }
    if (deviceName) {
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_DEVICE_NAME, deviceName);
    }
    
    // KRITISKT: Synka även Zustand cart-storen för att förhindra race condition
    // där gammalt deviceId i cart leder till QR-nyckel för fel enhet
    const cartStore = localStorage.getItem('trade-cart-storage');
    if (cartStore) {
      try {
        const parsed = JSON.parse(cartStore);
        if (parsed.state?.deviceId && parsed.state.deviceId !== deviceId) {
          console.warn('[DeviceStateManager] Zustand cart has different deviceId, syncing:', {
            old: parsed.state.deviceId,
            new: deviceId
          });
          parsed.state.deviceId = deviceId;
          // Om det var en annan enhet OCH det finns varor - rensa varor för säkerhet
          if (parsed.state.items?.length > 0) {
            console.warn('[DeviceStateManager] CRITICAL: Clearing cart items due to device change to prevent wrong QR key');
            parsed.state.items = [];
          }
          localStorage.setItem('trade-cart-storage', JSON.stringify(parsed));
        }
      } catch (parseError) {
        console.error('[DeviceStateManager] Failed to parse/sync cart store:', parseError);
      }
    }
  } catch (error) {
    console.error('[DeviceStateManager] Failed to save device:', error);
  }
}

/**
 * Hämtar enhets-ID - prioriterar sessionStorage
 */
export function getDeviceId(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.SELECTED_DEVICE_ID) 
      || localStorage.getItem(STORAGE_KEYS.CART_DEVICE_ID);
  } catch {
    return null;
  }
}

/**
 * Hämtar komplett enhetsinformation från storage
 */
export function getDeviceInfo(): DeviceInfo | null {
  try {
    const deviceId = getDeviceId();
    if (!deviceId) return null;
    
    const deviceCode = sessionStorage.getItem(STORAGE_KEYS.ONBOARDING_DEVICE_CODE) 
      || localStorage.getItem(STORAGE_KEYS.SELECTED_DEVICE_CODE);
    const deviceName = sessionStorage.getItem(STORAGE_KEYS.SELECTED_DEVICE_NAME);
    
    return {
      deviceId,
      deviceCode: deviceCode || undefined,
      deviceName: deviceName || undefined
    };
  } catch {
    return null;
  }
}

/**
 * Rensar alla enhetsrelaterade nycklar
 */
export function clearDeviceState(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.CART_DEVICE_ID);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_DEVICE_CODE);
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_DEVICE_ID);
    sessionStorage.removeItem(STORAGE_KEYS.ONBOARDING_DEVICE_CODE);
    sessionStorage.removeItem(STORAGE_KEYS.SELECTED_DEVICE_NAME);
  } catch (error) {
    console.error('[DeviceStateManager] Failed to clear device state:', error);
  }
}

/**
 * Validerar och synkar device state vid app-initialisering
 */
export function validateAndSyncDeviceState(): string | null {
  const sessionId = sessionStorage.getItem(STORAGE_KEYS.SELECTED_DEVICE_ID);
  const localId = localStorage.getItem(STORAGE_KEYS.CART_DEVICE_ID);
  
  if (sessionId && localId && sessionId !== localId) {
    localStorage.setItem(STORAGE_KEYS.CART_DEVICE_ID, sessionId);
    return sessionId;
  }
  
  if (sessionId && !localId) {
    localStorage.setItem(STORAGE_KEYS.CART_DEVICE_ID, sessionId);
    return sessionId;
  }
  
  if (localId && !sessionId) {
    sessionStorage.setItem(STORAGE_KEYS.SELECTED_DEVICE_ID, localId);
    return localId;
  }
  
  return sessionId || localId;
}
